import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  FILE_UPLOAD,
  MAX_UPLOAD_FILE_SIZE,
  CustomModalButtons,
  TRAIN_USAGE_MODAL_ACTIONS,
  STATUS_KEYS,
} from "../../../../constants/global.constants";
import createNotification, {
  ERROR_NOTIFICATION,
  SUCCESS_NOTIFICATION,
} from "../../../../components/Notifications";
import CircularLoader from "../../../../common/styles/components/circularLoader";
import CustomModal from "../../../../packages/CustomModal";
import WPTertiaryButton from "../../../../packages/WPTertiaryButton";
import {
  deleteUploadedFile,
  uploadSelectedFile,
} from "../../../../network/cipAPI";
import { create_sfdc_engagement } from "../../../../utils/SFDCexternalScript";
import { SFDC_ENGAGEMENT_API_URL } from "../../../../utils/constantsString";
import { getSupportedFileTypes } from "../../../../utils/appUtils";
import {
  extractPlantId,
  getAutoRenamedUploadFileName,
} from "../../../../utils/Utils";
import {
  FilesTypeIcon,
  FileSheetMappingIcon,
  NewBadgeIcon,
  UploadIcon,
} from "../../../../assets/icons";

import DatasetUploadModal from "./datasetUploadModal";

import {
  getUploadedFileCards,
  getUploadValidationError,
} from "./datasetUploadUtils";
import FileReplaceDeleteModal from "./fileReplaceDeleteModal";
import UploadedFilesList from "./uploadedDatasetsList";
import { useFileSheetMappingFlow } from "./fileSheetMappingFlow";
import "./index.scss";

const SUPPORTED_UPLOAD_EXTENSIONS = getSupportedFileTypes(
  FILE_UPLOAD.SUPPORTED_FILE_TYPES,
  "",
  true,
).split(", ");

const DatasetUpload = ({
  fileInfo,
  uploadedFiles,
  totalTrainCount,
  selectedTrain,
  sheetNames,
  allTrainsStatus,
  setTempSelectedTrain,
  onRefreshTrainTabs,
}) => {
  const supportedUploadAccept = getSupportedFileTypes(
    FILE_UPLOAD.SUPPORTED_FILE_TYPES,
    ".",
    true,
  );
  const { t } = useTranslation();
  const { userEmail, plantData } = useSelector((state) => ({
    userEmail: state.userEmail,
    plantData: state.plantData,
  }));
  const datasetInputRef = useRef(null);
  const uploadQueueIdRef = useRef(0);
  const isInitialSelectionRef = useRef(true);
  const replaceErroredFileIndexRef = useRef(null);

  const [fileName, setFileName] = useState(fileInfo?.fileName);
  const [showMultiSheetDialog, setShowMultiSheetDialog] = useState(false);
  const [selectedUploadFiles, setSelectedUploadFiles] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [showTrainUsageModal, setShowTrainUsageModal] = useState(false);
  const [trainUsageModalAction, setTrainUsageModalAction] = useState("");
  const [selectedActionFileId, setSelectedActionFileId] = useState(null);
  const [isUploadSectionLoading, setIsUploadSectionLoading] = useState(false);
  const [expandedCardsById, setExpandedCardsById] = useState({});

  const plantId = extractPlantId();

  const isReplaceDeleteDisabled = useMemo(() => {
    const selectedPlant = Array.isArray(plantData)
      ? plantData.find((plant) => String(plant?.plant_id) === String(plantId))
      : null;
    return selectedPlant?.is_owner === false;
  }, [plantData, plantId]);

  useEffect(() => {
    setFileName(fileInfo?.fileName || null);
  }, [fileInfo?.fileName]);

  useEffect(() => {
    const files = Array.isArray(uploadedFiles) ? uploadedFiles : [];
    if (!files.length) return;

    setExpandedCardsById((prev) => {
      const next = { ...prev };
      let hasChanges = false;

      files.forEach((uploadedFile, index) => {
        const cardId = `${uploadedFile?.plant_file_id || index}`;
        if (!Object.prototype.hasOwnProperty.call(next, cardId)) {
          next[cardId] = false;
          hasChanges = true;
        }
      });

      return hasChanges ? next : prev;
    });
  }, [uploadedFiles]);

  const uploadedFileCards = getUploadedFileCards({
    uploadedFiles,
    fileInfo,
    fileName,
    sheetNames,
    selectedTrain,
  });
  const hasUploadedFiles = uploadedFileCards.length > 0;
  const isUploadLimitReached = uploadedFileCards.length >= totalTrainCount;
  const isReplacingUploadedFile =
    trainUsageModalAction === TRAIN_USAGE_MODAL_ACTIONS.REPLACE &&
    Boolean(selectedActionFileId);
  const uploadedAndSelectedFilesCount =
    selectedUploadFiles.length +
    (uploadedFiles?.length ?? 0) -
    (isReplacingUploadedFile ? 1 : 0);

  const hasMultiSheetsInSelectedFiles = selectedUploadFiles.some(
    (uploadFileItem) => uploadFileItem.hasMultipleSheets,
  );
  const hasSuccessInSelectedFiles = selectedUploadFiles.some(
    (uploadFileItem) => uploadFileItem.status === STATUS_KEYS.SUCCESS,
  );

  const openDatasetFilePicker = (
    isInitialSelection = true,
    isReplace = false,
  ) => {
    isInitialSelectionRef.current = isInitialSelection;
    if (datasetInputRef.current) {
      if (isReplace) {
        datasetInputRef.current.multiple = false;
      } else {
        datasetInputRef.current.multiple = true;
        setSelectedActionFileId(null);
      }
      datasetInputRef.current.click();
    }
  };

  const handleAddFileClick = () => {
    openDatasetFilePicker(true);
  };

  const handleAddMoreFiles = () => {
    openDatasetFilePicker(false);
  };

  const handleSelectedFileReplace = (indexToReplace) => {
    replaceErroredFileIndexRef.current = indexToReplace;
    openDatasetFilePicker(false, true);
  };

  const { openFileSheetMappingFlow, renderFileSheetMappingDialogs } =
    useFileSheetMappingFlow({
      plantId,
      sheetNames,
    });

  const handleFileSheetMappingClick = () => {
    openFileSheetMappingFlow();
  };

  const handleExpandCollapseCard = (cardId) => {
    setExpandedCardsById((prev) => ({
      ...prev,
      [cardId]: !prev[cardId],
    }));
  };

  const resetTrainUsageModalState = () => {
    setShowTrainUsageModal(false);
    setTrainUsageModalAction("");
    setSelectedActionFileId(null);
  };

  const handleDeleteUploadedFileApi = async (targetPlantFileId) => {
    if (targetPlantFileId == null || targetPlantFileId === "") {
      return;
    }

    setIsUploadSectionLoading(true);
    try {
      await deleteUploadedFile(targetPlantFileId);

      setExpandedCardsById((prev) => {
        const next = { ...prev };
        delete next[String(targetPlantFileId)];
        return next;
      });

      onRefreshTrainTabs();

      createNotification(
        SUCCESS_NOTIFICATION,
        t("notificationStrings.deleteSuccess"),
        "",
      );
    } catch (deleteError) {
      createNotification(
        ERROR_NOTIFICATION,
        deleteError?.detail?.error || t("notificationStrings.deleteFail"),
        "",
      );
    } finally {
      setIsUploadSectionLoading(false);
    }
  };

  const resetUploadModalState = () => {
    setIsUploadModalOpen(false);
    setSelectedUploadFiles([]);
    setSelectedActionFileId(null);
  };

  const hasValidPlantFileId = (cardId) => cardId || cardId === 0;

  const getNormalizedPlantFileId = (cardId) => {
    const parsedPlantFileId = Number(cardId);
    return Number.isNaN(parsedPlantFileId) ? cardId : parsedPlantFileId;
  };

  const hasTrainUsageForCard = (uploadedCard) =>
    (uploadedCard?.sheets || []).some((sheet) => {
      const trainNums = Array.isArray(sheet?.trainNums) ? sheet.trainNums : [];
      return trainNums.some((trainNum) => {
        const normalizedTrainNum = Number(trainNum);
        return Number.isInteger(normalizedTrainNum) && normalizedTrainNum > 0;
      });
    });

  const handleReplaceUploadedFile = (uploadedCard) => {
    if (isReplaceDeleteDisabled || !hasValidPlantFileId(uploadedCard?.id)) {
      return;
    }

    const normalizedPlantFileId = getNormalizedPlantFileId(uploadedCard?.id);
    const hasTrainUsage = hasTrainUsageForCard(uploadedCard);

    setSelectedActionFileId(normalizedPlantFileId);
    setTrainUsageModalAction(TRAIN_USAGE_MODAL_ACTIONS.REPLACE);

    if (!hasTrainUsage) {
      setShowTrainUsageModal(false);
      openDatasetFilePicker(false, true);
      return;
    }

    setShowTrainUsageModal(true);
  };

  const handleDeleteUploadedFile = async (uploadedCard) => {
    if (isReplaceDeleteDisabled || !hasValidPlantFileId(uploadedCard?.id)) {
      return;
    }

    const normalizedPlantFileId = getNormalizedPlantFileId(uploadedCard?.id);
    const hasTrainUsage = hasTrainUsageForCard(uploadedCard);

    if (!hasTrainUsage) {
      await handleDeleteUploadedFileApi(normalizedPlantFileId);
      return;
    }

    setSelectedActionFileId(normalizedPlantFileId);
    setTrainUsageModalAction(TRAIN_USAGE_MODAL_ACTIONS.DELETE);
    setShowTrainUsageModal(true);
  };

  const handleTrainUsageModalAction = async ({ target } = {}) => {
    const action = target?.id;

    if (
      action === CustomModalButtons.CLOSE ||
      action === CustomModalButtons.CANCEL
    ) {
      resetTrainUsageModalState();
      return;
    }

    if (action === CustomModalButtons.CONFIRM) {
      if (trainUsageModalAction === TRAIN_USAGE_MODAL_ACTIONS.DELETE) {
        const targetPlantFileId = selectedActionFileId;
        resetTrainUsageModalState();
        await handleDeleteUploadedFileApi(targetPlantFileId);
        return;
      }

      setShowTrainUsageModal(false);
      openDatasetFilePicker(false, true);
    }
  };

  const handleAppendFromTrainUsageTable = (appendClickedTrain) => {
    setTempSelectedTrain(appendClickedTrain);
    resetTrainUsageModalState();
  };

  const uploadFileInChunks = async (selectedFile, targetFileName, uploadId) => {
    const chunkSize = 3 * 1024 * 1024; // 3MB per chunk
    const totalChunks = Math.ceil(selectedFile.size / chunkSize);
    const selectedFileName = targetFileName || selectedFile.name;

    const promiseList = [];
    for (let chunkIndex = 0; chunkIndex < totalChunks - 1; chunkIndex++) {
      const chunk = selectedFile.slice(
        chunkIndex * chunkSize,
        (chunkIndex + 1) * chunkSize,
      );
      const formData = new FormData();
      formData.append("File", chunk, selectedFileName);

      promiseList.push(
        uploadSelectedFile(
          formData,
          chunkIndex + 1,
          totalChunks,
          selectedTrain,
          false,
          selectedActionFileId,
        ),
      );
    }

    await Promise.all(promiseList);

    // Upload last chunk, then mark upload as complete.
    const chunk = selectedFile.slice(
      (totalChunks - 1) * chunkSize,
      totalChunks * chunkSize,
    );
    const formData = new FormData();
    formData.append("File", chunk, selectedFileName);

    await uploadSelectedFile(
      formData,
      totalChunks,
      totalChunks,
      selectedTrain,
      false,
      selectedActionFileId,
    );

    const response = await uploadSelectedFile(
      formData,
      totalChunks,
      totalChunks,
      selectedTrain,
      true,
      selectedActionFileId,
    );

    if (response?.status !== 200 || !response || response.file_id === 0) {
      throw new Error(
        response?.detail?.error || t("notificationStrings.somethingWrong"),
      );
    } else {
      setSelectedUploadFiles((prev) =>
        prev.map((file) =>
          file.uploadId === uploadId
            ? {
                ...file,
                status: STATUS_KEYS.SUCCESS,
                error: "",
                hasMultipleSheets: response?.sheet_names?.length > 1,
              }
            : file,
        ),
      );
    }

    // Create Salesforce engagement for data upload.
    if (SFDC_ENGAGEMENT_API_URL && selectedFile) {
      create_sfdc_engagement({
        engagementType: "Technical",
        engagementSubType: "Assessment",
        engagementName: selectedFile.name,
        engagementURL: window.location.href,
        engagementCreationDate: moment().format("YYYY-MM-DDTHH:mm:ssZ"),
        originURL: window.location.href,
        emailAddress: userEmail,
      });
    }

    return response;
  };

  const addFilesToUploadQueue = (selectedFiles = []) => {
    const isInitialSelection = isInitialSelectionRef.current;
    const replaceErroredFileIndex = replaceErroredFileIndexRef.current;
    const isReplaceSelectedFile = replaceErroredFileIndex !== null;

    const toLower = (value) => (value || "").toLowerCase();

    const uploadedNameList = uploadedFileCards.map((card) => card?.fileName);
    const selectedQueueNameList = selectedUploadFiles
      .map(
        (uploadFileItem) =>
          uploadFileItem?.resolvedName || uploadFileItem?.file?.name,
      )
      .filter(
        (uploadedFileItem, index) =>
          !isReplaceSelectedFile || index !== replaceErroredFileIndex,
      )
      .map(toLower);

    const duplicateFileNames = [];
    const uploadQueueItems = [];

    selectedFiles.forEach((selectedFile) => {
      const inputFileName = selectedFile?.name || "";
      const resolvedFileName = getAutoRenamedUploadFileName(
        inputFileName,
        uploadedNameList,
      );

      const conflictingName = selectedQueueNameList.includes(
        toLower(inputFileName),
      )
        ? inputFileName
        : selectedQueueNameList.includes(toLower(resolvedFileName))
          ? resolvedFileName
          : "";

      if (!isInitialSelection && conflictingName) {
        duplicateFileNames.push(conflictingName);
        return;
      }

      selectedQueueNameList.push(toLower(resolvedFileName));

      if (!isReplaceSelectedFile) {
        uploadQueueIdRef.current += 1;

        uploadQueueItems.push({
          uploadId: `upload-${uploadQueueIdRef.current}`,
          file: selectedFile,
          resolvedName: resolvedFileName,
          hasMultipleSheets: false,
        });
      } else {
        uploadQueueItems.push({
          file: selectedFile,
          resolvedName: resolvedFileName,
        });
      }
    });

    duplicateFileNames.forEach((fileName) => {
      createNotification(
        ERROR_NOTIFICATION,
        t("analyseData.fileAlreadyExists", { fileName }),
        "",
      );
    });

    if (!uploadQueueItems.length) {
      return;
    }

    setSelectedUploadFiles((prevFiles) => {
      if (isReplaceSelectedFile) {
        const replacementItem = uploadQueueItems[0];
        return prevFiles.map((uploadFileItem, index) =>
          index === replaceErroredFileIndex
            ? {
                ...uploadFileItem,
                file: replacementItem.file,
                resolvedName: replacementItem.resolvedName,
                status: null,
                error: "",
                hasMultipleSheets: false,
              }
            : uploadFileItem,
        );
      }

      return isInitialSelection
        ? uploadQueueItems
        : [...prevFiles, ...uploadQueueItems];
    });
    setIsUploadModalOpen(true);

    isInitialSelectionRef.current = false;
    replaceErroredFileIndexRef.current = null;
  };

  const handleFileSelection = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    addFilesToUploadQueue(selectedFiles);
    e.target.value = "";
  };

  const handleUploadModalAction = async ({ target } = {}) => {
    const action = target?.id;

    if (action === CustomModalButtons.CANCEL) {
      if (hasSuccessInSelectedFiles) {
        if (hasMultiSheetsInSelectedFiles) {
          setShowMultiSheetDialog(true);
        }

        onRefreshTrainTabs();
      }

      resetUploadModalState();
      return;
    }

    if (action === CustomModalButtons.CONFIRM) {
      handleUploadFiles();
    }
  };

  const handleSelectedFileRemove = (indexToRemove) => {
    setSelectedUploadFiles((prevFiles) => {
      const nextFiles = prevFiles.filter((_, index) => index !== indexToRemove);
      if (!nextFiles.length) {
        resetUploadModalState();
      }
      return nextFiles;
    });
  };

  const handleUploadFiles = async () => {
    const validatedUploadFiles = selectedUploadFiles.map(
      (uploadQueueItem, index) => {
        const validationError = getUploadValidationError(
          uploadQueueItem?.file,
          SUPPORTED_UPLOAD_EXTENSIONS,
          MAX_UPLOAD_FILE_SIZE,
        );

        return {
          ...uploadQueueItem,
          uploadId: uploadQueueItem.uploadId || `upload-legacy-${index}`,
          hasMultipleSheets: uploadQueueItem.hasMultipleSheets || false,
          error: validationError ? t(validationError) : "",
        };
      },
    );

    setSelectedUploadFiles(validatedUploadFiles);

    if (validatedUploadFiles.some((uploadQueueItem) => uploadQueueItem.error)) {
      return;
    }

    const uploadableFiles = validatedUploadFiles.filter(
      (uploadQueueItem) => uploadQueueItem.status !== STATUS_KEYS.SUCCESS,
    );

    if (!uploadableFiles.length) {
      return;
    }

    setSelectedUploadFiles(
      validatedUploadFiles.map((uploadQueueItem) =>
        uploadQueueItem.status === STATUS_KEYS.SUCCESS
          ? uploadQueueItem
          : {
              ...uploadQueueItem,
              status: STATUS_KEYS.LOADING,
              error: "",
            },
      ),
    );

    const uploadResults = await Promise.allSettled(
      uploadableFiles.map((uploadQueueItem) =>
        uploadFileInChunks(
          uploadQueueItem?.file,
          uploadQueueItem?.resolvedName,
          uploadQueueItem.uploadId,
        ),
      ),
    );

    const hasFailedUpload = uploadResults.some(
      (uploadResult) => uploadResult.status === "rejected",
    );

    if (!hasFailedUpload) {
      return;
    }

    const failedUploadMap = uploadResults.reduce((acc, uploadResult, i) => {
      if (uploadResult.status === "rejected") {
        acc[uploadableFiles[i].uploadId] =
          uploadResult?.reason?.detail?.error ||
          uploadResult?.reason?.message ||
          t("notificationStrings.somethingWrong");
      }
      return acc;
    }, {});

    setSelectedUploadFiles((prev) =>
      prev.map((uploadQueueItem) => {
        const errorMessage = failedUploadMap[uploadQueueItem.uploadId];
        if (!errorMessage) {
          return uploadQueueItem;
        }

        return {
          ...uploadQueueItem,
          status: STATUS_KEYS.ERROR,
          error: errorMessage,
        };
      }),
    );
  };

  const handleMultiSheetDialogClose = () => {
    setShowMultiSheetDialog(false);
  };

  return (
    <>
      <input
        ref={datasetInputRef}
        type="file"
        onChange={handleFileSelection}
        accept={supportedUploadAccept}
        multiple
        style={{ display: "none" }}
      />
      <div className="upload-left-panel">
        <div className="upload-section-title">
          {t("analyseData.uploadedData")}
        </div>

        <div className="upload-left-panel__content-area">
          {isUploadSectionLoading && (
            <div className="circular-loader-overlay upload-left-panel__loader-overlay">
              <CircularLoader />
            </div>
          )}

          {!uploadedFileCards.length && (
            <div className="upload-empty-state">
              <div className="upload-empty-visual upload-empty-visual--svg">
                <FilesTypeIcon className="upload-empty-illustration" />
              </div>
              <div className="upload-empty-title">
                {t("analyseData.noFilesUploaded")}
              </div>
              <div className="upload-empty-subtitle">
                {t("analyseData.uploadedDataPlaceholder")}
              </div>
            </div>
          )}

          <UploadedFilesList
            uploadedFileCards={uploadedFileCards}
            isReplaceDeleteDisabled={isReplaceDeleteDisabled}
            isActionLoading={isUploadSectionLoading}
            expandedCardsById={expandedCardsById}
            onToggleCard={handleExpandCollapseCard}
            onReplaceFile={handleReplaceUploadedFile}
            onDeleteFile={handleDeleteUploadedFile}
            t={t}
          />

          <div
            className={`upload-action-row${
              !hasUploadedFiles ? " upload-action-row--single" : ""
            }`}
          >
            {hasUploadedFiles && (
              <div className="upload-map-btn-wrapper">
                <WPTertiaryButton
                  id="file-sheet-mapping-btn"
                  onClick={handleFileSheetMappingClick}
                  className="upload-map-btn"
                  icon={
                    <FileSheetMappingIcon className="upload-map-btn-icon" />
                  }
                  iconPlacement="before"
                  label={t("fileSheetMapping.title")}
                  variant="outlined"
                  type="secondary"
                  size="small"
                />
                <NewBadgeIcon className="upload-map-new-badge" />
              </div>
            )}

            <WPTertiaryButton
              id="upload-files-btn"
              onClick={handleAddFileClick}
              disabled={isUploadLimitReached}
              tooltip={
                isUploadLimitReached
                  ? t("analyseData.maxUploadLimitReached")
                  : ""
              }
              showTooltipOnDisabled
              className="upload-files-btn"
              icon={<UploadIcon className="upload-files-btn-icon" />}
              iconPlacement="before"
              label={t("analyseData.uploadFiles")}
              variant="contained"
              size="small"
            />
          </div>
        </div>
      </div>

      <FileReplaceDeleteModal
        isOpen={showTrainUsageModal}
        trainUsageModalAction={trainUsageModalAction}
        uploadedFiles={uploadedFiles}
        allTrainsStatus={allTrainsStatus}
        selectedActionFileId={selectedActionFileId}
        onModalClose={handleTrainUsageModalAction}
        onAppendFromTrainUsageTable={handleAppendFromTrainUsageTable}
      />
      {isUploadModalOpen && (
        <DatasetUploadModal
          selectedUploadFiles={selectedUploadFiles}
          totalTrainCount={totalTrainCount}
          onModalClose={handleUploadModalAction}
          uploadedAndSelectedFilesCount={uploadedAndSelectedFilesCount}
          onAddMoreFiles={handleAddMoreFiles}
          onReplace={handleSelectedFileReplace}
          onRemove={handleSelectedFileRemove}
          hideAddMore={isReplacingUploadedFile}
        />
      )}

      {showMultiSheetDialog && (
        <CustomModal
          header={t("analyseData.excelProcessing")}
          onModalClose={handleMultiSheetDialogClose}
          confirmBtn={t("ValueStrings.okay")}
          width="575px"
          hideCloseIcon
        >
          <div className="optionalDescriptionText">
            <span>{t("analyseData.multiSheetMessage")}</span>{" "}
            <span>{t("analyseData.multiFileMessage")}</span>
          </div>
        </CustomModal>
      )}

      {renderFileSheetMappingDialogs()}
    </>
  );
};

export default DatasetUpload;
